/**
 * The one error type the platform throws. Every adapter maps its
 * provider-specific failures onto a typed {@link PlatformErrorCode} so callers
 * (screens, services) branch on a stable code — never on a Firebase/Colyseus
 * error shape.
 */

export type PlatformErrorCode =
  | 'auth'
  | 'network'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'permission'
  /** A usage limit was reached (e.g. the per-creator daily build cap). Terminal
   *  for this request; retrying later — after the window resets — can succeed. */
  | 'quota'
  /** A precondition the caller can satisfy is missing — e.g. flipping a
   *  creation public before its active version carries a rendered-audit
   *  stamp. `details` names what to do (the auto-enqueued audit jobId). */
  | 'audit-required'
  | 'unavailable'
  | 'internal';

/**
 * The sanctioned platform error. `retryable` tells callers whether a bounded
 * retry could succeed (transient network/unavailable) vs a terminal failure
 * (validation/permission/not-found).
 */
export class PlatformError extends Error {
  readonly code: PlatformErrorCode;
  readonly retryable: boolean;
  /** Optional wrapped cause (the original adapter error) for logs. */
  override readonly cause?: unknown;
  /** Optional structured payload for the CALLER (surfaced through the
   *  callable error's `details`) — e.g. `audit-required` carries the
   *  auto-enqueued audit `jobId`. Never secrets; this crosses the wire. */
  readonly details?: Record<string, unknown>;

  constructor(
    code: PlatformErrorCode,
    message: string,
    opts?: { retryable?: boolean; cause?: unknown; details?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'PlatformError';
    this.code = code;
    // Default retryability by code when the caller doesn't state it: only the
    // two genuinely-transient codes retry by default.
    this.retryable = opts?.retryable ?? (code === 'network' || code === 'unavailable');
    this.cause = opts?.cause;
    if (opts?.details) this.details = opts.details;
    // Restore the prototype chain (transpile targets below ES2015 semantics).
    Object.setPrototypeOf(this, PlatformError.prototype);
  }
}

/** Narrowing guard — true when `e` is a {@link PlatformError}. */
export function isPlatformError(e: unknown): e is PlatformError {
  return e instanceof PlatformError;
}
